import { useCallback, useId, useRef, type ReactNode } from 'react';
import { cn } from '@/lib/cn';

export type SegmentTone = 'neutral' | 'success' | 'danger' | 'primary';

export interface SegmentOption<T extends string> {
  value: T;
  label: ReactNode;
  /** Accessible name when `label` is an icon or abbreviation. */
  title?: string;
  tone?: SegmentTone;
  disabled?: boolean;
}

const TONE_ACTIVE: Record<SegmentTone, string> = {
  neutral: 'bg-surface text-foreground shadow-soft',
  success: 'bg-surface text-success shadow-soft',
  danger: 'bg-surface text-danger shadow-soft',
  primary: 'bg-surface text-primary shadow-soft',
};

/**
 * A small set of mutually exclusive choices, rendered inline.
 *
 * Exists because dense tables were using a `<Select>` per cell: the access
 * matrix alone rendered ~240 dropdowns, so reading a row meant opening menus
 * one at a time. Inline segments make the current state legible at a glance
 * and settable in one click.
 *
 * Implements the roving-tabindex radiogroup pattern: one tab stop for the
 * whole control, arrow keys move between options.
 */
export function SegmentedControl<T extends string>({
  value,
  onChange,
  options,
  ariaLabel,
  size = 'sm',
  className,
}: {
  value: T;
  onChange: (next: T) => void;
  options: SegmentOption<T>[];
  ariaLabel: string;
  size?: 'xs' | 'sm';
  className?: string;
}) {
  const groupId = useId();
  const ref = useRef<HTMLDivElement>(null);

  const move = useCallback(
    (delta: number) => {
      const enabled = options.filter((o) => !o.disabled);
      if (enabled.length === 0) return;
      const idx = enabled.findIndex((o) => o.value === value);
      const next = enabled[(idx + delta + enabled.length) % enabled.length];
      if (!next) return;
      onChange(next.value);
      ref.current?.querySelector<HTMLElement>(`[data-segment="${next.value}"]`)?.focus();
    },
    [onChange, options, value],
  );

  return (
    <div
      ref={ref}
      role="radiogroup"
      aria-label={ariaLabel}
      className={cn('inline-flex shrink-0 gap-0.5 rounded-md bg-muted p-0.5', className)}
      onKeyDown={(e) => {
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
          e.preventDefault();
          move(1);
        } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
          e.preventDefault();
          move(-1);
        }
      }}
    >
      {options.map((option) => {
        const active = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            id={`${groupId}-${option.value}`}
            data-segment={option.value}
            aria-checked={active}
            aria-label={option.title}
            title={option.title}
            disabled={option.disabled}
            tabIndex={active ? 0 : -1}
            onClick={() => onChange(option.value)}
            className={cn(
              'press rounded-sm font-semibold uppercase tracking-wide transition-colors duration-150 focus-ring disabled:pointer-events-none disabled:opacity-40',
              size === 'xs' ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-1 text-[11px]',
              active ? TONE_ACTIVE[option.tone ?? 'neutral'] : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
